import styles from './PlanDetails.module.css';
import helpers from '../../helpers.js';
import Module from '../Module.js';
import Gauge from '../subscription/Gauge.js';
import { useState, useContext, useEffect, Fragment } from 'react';
import { UserContext } from '../../UserContext.js';
import Button from "../Button.js";
import Overlay from "../Overlay.js";
// import PlanDetailsSummary from "./PlanDetailsSummary.js";
import ApplicationCard from "./ApplicationCard.js";
import iconGrassland from "../../assets/images/iconGrassland.png";
import iconArable from '../../assets/images/iconArable.png';


import TextInput from "../TextInput.js";
import Selector from "../../components/Selector";
import Checkbox from '../Checkbox.js';
import KeyFigures from '../KeyFigures.js';

// BBB-521. A `targeted_zones` entry carries `_zone` in one of three shapes, and
// this file used to assume the first:
//
//   1. a populated Zone document, which is what plan.service.js:122 normally
//      returns via .populate({ path: "targeted_zones._zone", model: "Zone" });
//   2. a bare ObjectId string, when the caller did not populate;
//   3. null, when the referenced Zone document is genuinely absent.
//
// Reading `zone._zone.zone_name` throws on both 2 and 3, and this component
// already knew that: getAllApplicationsFromZones uses `tz._zone?._id` and
// `tz._zone?.zone_name`. The guard was just applied in one place out of nine.
//
// WHAT THE TICKET GETS WRONG, checked in bbb-server rather than assumed.
// BBB-521 says deleting a zone produces shape 3. It does not. zone.service.js
// deleteZone is a SOFT delete: it sets is_deleted = true and saves. There is no
// pre-find middleware on the Zone schema and no `match` on the populate, so a
// deleted zone still populates as a full document. Shape 3 therefore comes from
// legacy or hand-edited records, not from anything a farmer can do in the app.
// That makes this hardening rather than a live crash fix, and it is why the
// ticket's High priority overstates it.
//
// A REAL defect falls out of that same finding and is NOT fixed here, because it
// is a product decision rather than an engineering one: since the populate does
// not filter is_deleted, a deleted zone keeps appearing in a plan as though it
// were live. Recorded on the ticket.
export const zoneRefId = (targetedZone) =>
  targetedZone?._zone?._id || targetedZone?._zone || null;

// Confirmed by Davin 11/09/2026. Only reachable for a zone whose record has
// gone missing, which should be never.
export const MISSING_ZONE_NAME = 'Field unavailable';

export const zoneRefName = (targetedZone) =>
  targetedZone?._zone?.zone_name || MISSING_ZONE_NAME;

// Mongoose defaults `targeted_zones: [targetedZoneSchema]` to [], so any plan
// written through the schema has an array. Legacy records predate that promise.
export const targetedZonesOf = (plan) =>
  Array.isArray(plan?.targeted_zones) ? plan.targeted_zones : [];

const PlanDetails = (props) => {
  const { user } = useContext(UserContext);
  const [formData, setFormData] = useState({});
  const [displayDeleteOverlay, setDisplayDeleteOverlay] = useState(false);
  const [visibleZones, setVisibleZones] = useState({});

  // Initialize visible zones when plan changes
  useEffect(() => {
    if (props.selectedPlan?.targeted_zones) {
      const initialState = {};
      targetedZonesOf(props.selectedPlan).forEach((zone) => {
        const zoneKey = zoneRefId(zone);
        if (zoneKey) initialState[zoneKey] = true; // All zones visible by default
      });
      setVisibleZones(initialState);
    }
  }, [props.selectedPlan]);

  // const [formData, setFormData] = useState({});
  if (!props.selectedPlan) return null;

  /*
  const handleChange = (formField, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [formField]: formField === "surfaceArea" && value !== "" && isNaN(value) 
        ? prevData.surfaceArea // Keep previous valid value if input is invalid
        : value, // Otherwise, store new value
    }));
  };
  */


  /*const renderOverlay = () => {
    if(!displayDeleteOverlay){
      return;
    }
    return(
      <Overlay>
        <div className={styles.deleteZoneContainer}>
           <h2>Delete Zone?</h2>
          <p>Are you sure you want to delete this zone? Type "<b>{props.selectedZone.zone_name}</b>" to delete zone</p>
          <div className={styles.deleteInput}>
            <TextInput 
              name="Zone ID" 
              value={formData.overlayInput} 
              onChange={(value) => handleChange("overlayInput", value)} 
              labelStyle="top" 
              placeholder="Enter Zone ID"
            />
            </div>
          <Button name={"Delete Zone"} disabled={props.selectedZone.zone_name === formData.overlayInput ? false : true} color={"Error"} onPress={() => deleteZone()}/>
          <Button name={"Cancel"}  color={"Coal"} onPress={() => setDisplayDeleteOverlay(false)}/>
        </div>
      </Overlay>
    ) 
  }*/
  // Calculate the color intensity for application rates
  const getApplicationRateColor = (appliedRate, targetRate) => {
    const ratio = appliedRate / targetRate;
    if (ratio >= 1) return "#34B61F"; // Dark green if applied rate meets or exceeds target
    return `rgba(52, 182, 31, ${Math.max(ratio, 0.2)})`; // Adjust opacity based on ratio
  };

  const renderOverlay = () => {
    if(!displayDeleteOverlay){
      return;
    }
    return(
      <Overlay>
        <div className={styles.deleteZoneContainer}>
           <h2>Delete Plan?</h2>
          <p>Are you sure you want to delete this plan? Type "<b>{props.selectedPlan.plan_name}</b>" to delete plan.</p>
          <div className={styles.deleteInput}>
            <TextInput 
              name="Plan Name" 
              value={formData.overlayInput} 
              onChange={(value) => handleChange("overlayInput", value)} 
              labelStyle="top" 
              placeholder="Enter Plan Name"
            />
            </div>
          <Button name={"Delete Plan"} disabled={props.selectedPlan.plan_name === formData.overlayInput ? false : true} color={"Error"} onPress={() => deletePlan()}/>
          <Button name={"Cancel"}  color={"Coal"} onPress={() => setDisplayDeleteOverlay(false)}/>
        </div>
      </Overlay>
    ) 
  }

  const renderHeader = (header, value, headerWidth) => {
    return ( 
      <div className={styles.headerLineContainer}>
        <div className={styles.headerContainer} style={{width: headerWidth}}>
              {header}
              {value && <div className={styles.headerValue}>{value}</div>}
        </div>
      </div>
    );
  };

  const handleChange = (formField, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [formField]: formField === "surfaceArea" && value !== "" && isNaN(value) 
        ? prevData.surfaceArea // Keep previous valid value if input is invalid
        : value, // Otherwise, store new value
    }));
  };

  const toggleZoneVisibility = (zoneId) => {
    setVisibleZones((prev) => ({
      ...prev,
      [zoneId]: !prev[zoneId]
    }));
  };

  const deletePlan = async () => {
    try {
      
      const request = {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.token,
        }
      };

      // Send request
      const response = await fetch(user.backEndURL + "/plans/" + props.selectedPlan._id, request);
      const jsonResponse = await response.json();

      if (jsonResponse.success) {
        setDisplayDeleteOverlay(false);
        props.reloadZones();
        // helpers.successPrompt("Zone deleted successfully.", null, setPrompt, null);
      } else {
        alert("Oops! Something went wrong while deleting the zone.");
        // helpers.errorPrompt("Oops! Something went wrong while creating the zone.", null, setPrompt, null);
      }
    } catch (err) {
      console.error("Error creating zone:", err);
      // helpers.errorPrompt("Unexpected error occurred. Please try again.", null, setPrompt, null);
    }
  };

  const renderSummary = () => {
    if (!props.selectedPlan) return null;
  
    return (
      <div className={styles.summaryContainer}>
        {renderHeader("Summary")}
  
        <div className={helpers.clx(styles.summaryItem, styles.summaryItemDark)}>
          <div className={styles.summaryLabel}>Name</div>
          <div className={styles.summaryValue}>{props.selectedPlan.plan_name || "-"}</div>
        </div>
  
        {props.selectedPlan.plan_type === "livestock" ? (
          <>
            <div className={styles.summaryItem}>
              <div className={styles.summaryLabel}>Total Shed Area</div>
              <div className={styles.summaryValue}>
                {props.selectedPlan.targeted_zones ? 
                  `${targetedZonesOf(props.selectedPlan).reduce((sum, zone) => sum + (zone.total_shed_area || 0), 0).toFixed(2)} m²` : "-"}
              </div>
            </div>
            
            <div className={helpers.clx(styles.summaryItem, styles.summaryItemDark)}>
              <div className={styles.summaryLabel}>Product Type</div>
              <div className={styles.summaryValue}>
                {(props.selectedPlan._product?.name || props.selectedPlan.product_type || props.selectedPlan.productType) ? 
                  (() => {
                    const productType = props.selectedPlan._product?.name || props.selectedPlan.product_type || props.selectedPlan.productType;
                    return productType === "pinchar" ? "PinChar" : productType === "pelletchar" ? "PelletChar" : productType.charAt(0).toUpperCase() + productType.slice(1);
                  })() : 
                  "Not specified" }
              </div>
            </div>
            
            <div className={styles.summaryItem}>
              <div className={styles.summaryLabel}>Total Biochar</div>
              <div className={styles.summaryValue}>
                {props.selectedPlan.targeted_zones ? 
                  `${targetedZonesOf(props.selectedPlan).reduce((sum, zone) => {
                    const cycles = zone.cycles_per_year || 1;
                    const shedArea = zone.total_shed_area || 0;
                    const ratePerSqm = zone.targeted_rate_per_sqm || 0;
                    return sum + (ratePerSqm * shedArea * cycles / 1000);
                  }, 0).toFixed(2)} t` : "-"}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.summaryItem}>
              <div className={styles.summaryLabel}>Total Area</div>
              <div className={styles.summaryValue}>
                {props.selectedPlan.total_area ? `${props.selectedPlan.total_area.toFixed(2)} ha` : "-"}
              </div>
            </div>
            
            <div className={helpers.clx(styles.summaryItem, styles.summaryItemDark)}>
              <div className={styles.summaryLabel}>Product Type</div>
              <div className={styles.summaryValue}>
                {(props.selectedPlan._product?.name || props.selectedPlan.product_type || props.selectedPlan.productType) ? 
                  (() => {
                    const productType = props.selectedPlan._product?.name || props.selectedPlan.product_type || props.selectedPlan.productType;
                    return productType === "pinchar" ? "PinChar" : productType === "pelletchar" ? "PelletChar" : productType.charAt(0).toUpperCase() + productType.slice(1);
                  })() : 
                  "Not specified" }
              </div>
            </div>
            
            <div className={styles.summaryItem}>
              <div className={styles.summaryLabel}>Current Avg. Rate</div>
              <div className={styles.summaryValue}>
                {props.selectedPlan.applications? `${props.selectedPlan.average_target_rate.toFixed(2)} t/ha` : "n/a"}
              </div>
            </div>
            
            <div className={helpers.clx(styles.summaryItem, styles.summaryItemDark  )}>
              <div className={styles.summaryLabel}>Target Avg. Rate</div>
              <div className={styles.summaryValue}>
                {props.selectedPlan.average_target_rate ? `${props.selectedPlan.average_target_rate.toFixed(2)} t/ha` : "-"}
              </div>
            </div>
            
            <div className={helpers.clx(styles.summaryItem)}>
              <div className={styles.summaryLabel}>Year</div>
              <div className={styles.summaryValue}>
                {props.selectedPlan.plan_year || "-"}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Helper function to get all applications from zones in this plan
  const getAllApplicationsFromZones = () => {
    if (!props.selectedPlan?.targeted_zones) return [];
    
    const applications = [];
    
    // Create a map of zone IDs to zone names for easy lookup
    const zoneIdToName = {};
    if (props.zones) {
      props.zones.forEach(z => {
        zoneIdToName[z._id] = z.zone_name;
      });
    }
    targetedZonesOf(props.selectedPlan).forEach(tz => {
      const zoneId = tz._zone?._id || tz._zone;
      const zoneName = tz._zone?.zone_name;
      if (zoneId && zoneName) {
        zoneIdToName[zoneId] = zoneName;
      }
    });
    
    // Helper to convert zone IDs to names
    const convertToZoneNames = (targetedZones) => {
      return targetedZones.map(zoneRef => {
        // If it's already a name (not a MongoDB ID format), return as-is
        if (!zoneRef.match(/^[a-f\d]{24}$/i)) {
          return zoneRef;
        }
        // Otherwise, look up the name
        return zoneIdToName[zoneRef] || zoneRef;
      });
    };
    
    targetedZonesOf(props.selectedPlan).forEach((targetedZone, index) => {
      // Get the zone reference - could be populated or just an ID
      let zone = targetedZone._zone;
      const zoneId = zone?._id || zone;
      
      // If zone doesn't have applications, try to find it in the zones array passed via props
      // This handles cases where the plan's zone data isn't fully populated with applications
      if (!zone?.applications && props.zones) {
        const fullZone = props.zones.find(z => z._id === zoneId);
        if (fullZone) {
          zone = fullZone;
        }
      }
      
      if (zone && zone.applications && Array.isArray(zone.applications)) {
        // Add zone information to each application if not already present
        zone.applications.forEach(app => {
          // If the application doesn't have targetedZones, add the current zone
          if (!app.targetedZones || app.targetedZones.length === 0) {
            applications.push({
              ...app,
              targetedZones: [zone.zone_name || zone._id]
            });
          } else {
            // Convert any zone IDs to names
            applications.push({
              ...app,
              targetedZones: convertToZoneNames(app.targetedZones)
            });
          }
        });
      }
    });
    
    return applications;
  };

  const renderBreakdown = () => {
    if (!props.selectedPlan) return null;
    console.log(props.selectedPlan);
    
    // Check if this is a livestock plan
    if (props.selectedPlan.plan_type === "livestock") {
      // Calculate livestock-specific metrics
      const totalShedArea = targetedZonesOf(props.selectedPlan).reduce((sum, zone) => {
        return sum + (zone.total_shed_area || 0);
      }, 0);
      
      const totalBiocharT = targetedZonesOf(props.selectedPlan).reduce((sum, zone) => {
        const cycles = zone.cycles_per_year || 1;
        const shedArea = zone.total_shed_area || 0;
        const ratePerSqm = zone.targeted_rate_per_sqm || 0;
        return sum + (ratePerSqm * shedArea * cycles / 1000); // Convert kg to tonnes
      }, 0);
      
      const bagTonnes = (props.selectedPlan.product_type || props.selectedPlan.productType) === "pinchar" ? 0.215 : 0.65;
      const totalBags = Math.ceil(totalBiocharT / bagTonnes);
      
      return <KeyFigures data={[
        {label: "Total Shed Area", value: totalShedArea.toFixed(2), unit: "m²" },
        {label: "Total Biochar", value: totalBiocharT.toFixed(2), unit: "t" },
        {label: "Bags Equiv.", value: totalBags, unit: "bags" }
      ]}/>;
    }
    
    // Land plan calculations (existing logic)
    const totalArea = props.selectedPlan.total_area;
    const totalTargetKg = props.selectedPlan.average_target_rate * totalArea * 1000;
    const totalBags = Math.ceil(totalTargetKg / 215);
    const totalLorries = Math.round((totalBags  )/ 2.4)/10;
    console.log(totalArea + " " + totalTargetKg +  " ")

   return <KeyFigures data={[
      {label: "Total Area", value: totalArea.toFixed(2), unit: "ha" },
      {label: "Avg. Target Rate", value: props.selectedPlan.average_target_rate.toFixed(2), unit: "t/ha" },
      {label: "Bags Equiv.", value: totalBags, unit: "bags" },
      {label: "Artic Loads", value: totalLorries}
   ]}/>
  
  };

  // const renderBreakdown = () => {
  //   if (!formData || !zones || zones.length === 0) return null;
  
  //   const selected = selectedZones
  //     .map(index => zones[parseInt(index)])
  //     .filter(Boolean);
  
  //   const totalArea = selected.reduce((sum, zone) => {
  //     return sum + parseFloat(zone.surface_area || 0);
  //   }, 0);
  
  //   const totalTargetKg = selected.reduce((sum, zone) => {
  //     const rate = parseFloat(formData.targetedRates[zone._id] || 0); // ⬅️ FIXED
  //     return sum + rate * parseFloat(zone.surface_area || 0) * 1000;
  //   }, 0);
  
  //   const avgTargetRate = totalArea > 0 ? (totalTargetKg / totalArea / 1000).toFixed(2) : "0";
  //   const totalBags = Math.ceil(totalTargetKg / 215);
  //   const totalLorries = Math.round((totalBags  )/ 2.4)/10;
  
  //   return (
  //     <div className={styles.breakdownRow}>
  //       <div className={styles.breakdownItem}>
  //         <div className={styles.breakdownLabel}>Total Area</div>
  //         <div className={styles.breakdownValue}>{totalArea.toFixed(2)} <span className={styles.unit}>ha</span></div>
  //       </div>
  //       <div className={styles.separatorLine}/>
  
  //       <div className={styles.breakdownItem}>
  //         <div className={styles.breakdownLabel}>Avg. Target Rate</div>
  //         <div className={styles.breakdownValue}>{avgTargetRate} <span className={styles.unit}>t/ha</span></div>
  //       </div>
  //       <div className={styles.separatorLine}/>
  
  //       <div className={styles.breakdownItem}>
  //         <div className={styles.breakdownLabel}>Bags Equiv.</div>
  //         <div className={styles.breakdownValue}>{totalBags} <span className={styles.unit}>bags</span></div>
  //       </div>
  //       <div className={styles.separatorLine}/>
  
  //       <div className={styles.breakdownItem}>
  //         <div className={styles.breakdownLabel}>Artic Loads</div>
  //         <div className={styles.breakdownValue}>~ {totalLorries}</div>
  //       </div>
  //     </div>
  //   );
  // };

  return (
    <div className={helpers.clx(styles.container)}>
      {renderOverlay()}
      <div className={helpers.clx("contentGrid", styles.zoneContainer)}>
        <Module icon={'\uf274'} name={"Plan Details"} spanColumn={24} spanRow={1} height={"100%"}>
          <div className={styles.contentContainer}>
            <div className={styles.iconsContainer}>
              {/* <div className={helpers.clx(props.selectedPlan && styles.disabled, styles.editIcon)} onClick={() => setEditingInfo(true)}>
                {editingInfo ? "" : "pencil"}
              </div> */}
              <div className={styles.editIcon} onClick={() => setDisplayDeleteOverlay(true)}>
                {false ? "" : "trash-alt"}   
              </div>
            </div>
            <div className={styles.detailsRow}>
              
              {renderSummary()}
             
              <div className={styles.graphContainer}>
              {renderHeader("Key Figures")}
              {renderBreakdown()}
                {/* <div className={styles.doubleHeaderContainer}>
                  {renderHeader("Bags Applied")}
                  <div  className={styles.headerTotalContainer}>
                  {renderHeader("Total", "n/a", "10.5rem")}

                  </div>
                </div>
                <div className={styles.graphContent}>
                  <div className={styles.summaryLabel}>Feature Coming Soon ...</div>
                </div> */}
            
              </div>
            </div>
            
            <div className={styles.detailsRow}>
            {/* Applied Rate Gauges */}
              <div className={styles.gaugeContainer}>
              {renderHeader(props.selectedPlan.plan_type === "livestock" ? "Biochar Application" : "Current Applied Rate")}
                <div className={styles.gaugeContent}>
                {targetedZonesOf(props.selectedPlan).map((zone, index) => {
                  console.log(zone)
                  
                  if (props.selectedPlan.plan_type === "livestock") {
                    // For livestock plans, show biochar application metrics
                    const cycles = zone.cycles_per_year || 1;
                    const shedArea = zone.total_shed_area || 0;
                    const ratePerSqm = zone.targeted_rate_per_sqm || 0;
                    const zoneId = zoneRefId(zone);
                    const zoneName = zoneRefName(zone);
                    
                    // Calculate total shed area across all zones
                    const totalShedArea = targetedZonesOf(props.selectedPlan).reduce((sum, z) => sum + (z.total_shed_area || 0), 0);
                    
                    // Calculate this zone's proportion of total shed area
                    const areaRatio = totalShedArea > 0 ? shedArea / totalShedArea : 0;
                    
                    // Calculate total biochar for the entire plan
                    const totalPlanBiocharT = targetedZonesOf(props.selectedPlan).reduce((sum, z) => {
                      const zCycles = z.cycles_per_year || 1;
                      const zShedArea = z.total_shed_area || 0;
                      const zRatePerSqm = z.targeted_rate_per_sqm || 0;
                      return sum + (zRatePerSqm * zShedArea * zCycles / 1000);
                    }, 0);
                    
                    // Distribute biochar proportionally based on shed area ratio
                    const proportionalBiocharT = parseFloat((totalPlanBiocharT * areaRatio).toFixed(2));
                    
                    // Calculate applied biochar for this zone from applications
                    const allApplications = getAllApplicationsFromZones();
                    const appliedBiocharKg = allApplications.reduce((sum, app) => {
                      // Check if this application targets this zone
                      if (app.targetedZones && app.targetedZones.some(tz => tz === zoneId || tz === zoneName)) {
                        return sum + (app.weight || 0);
                      }
                      return sum;
                    }, 0);
                    const appliedBiocharT = parseFloat((appliedBiocharKg / 1000).toFixed(2));
                    
                    return(
                      <Fragment key={zoneRefId(zone)}>
                        {index !== 0 && <div className={styles.separatorLine}/>}
                        <div className={styles.gaugeItem}>
                          <Gauge progress={appliedBiocharT} progressMax={proportionalBiocharT} unit={"t"}/>
                          <div className={styles.gaugeLabel}>
                            <div className={styles.fieldType}>
                              <span className={styles.zoneTypeIcon}>home</span>
                              {zoneRefName(zone)}
                            </div>
                            <div className={styles.targetRate}>
                              Shed Area: <span className={styles.targetValue}>{shedArea} m²</span>
                            </div>
                            <div className={styles.targetRate}>
                              Num. Cycles: <span className={styles.targetValue}>{cycles}</span>
                            </div>
                            {/* <div className={styles.targetRate}>
                              Area Ratio: <span className={styles.targetValue}>{(areaRatio * 100).toFixed(1)}%</span>
                            </div> */}
                          </div>
                          <div className={styles.gaugeCheckboxContainer}>
                            <Checkbox 
                              checked={visibleZones[zoneId] !== false}
                              text="Show Applications"
                              onPress={() => toggleZoneVisibility(zoneId)}
                            />
                          </div>
                        </div>
                      </Fragment>
                    );
                  } else {
                    // For land plans, show the existing applied rate gauges
                    const zoneId = zoneRefId(zone);
                    const zoneName = zoneRefName(zone);
                    const zoneSurfaceArea = zone?._zone?.surface_area || 0;
                    
                    // Calculate applied biochar for this zone from applications
                    const allApplications = getAllApplicationsFromZones();
                    const appliedBiocharKg = allApplications.reduce((sum, app) => {
                      // Check if this application targets this zone
                      if (app.targetedZones && app.targetedZones.some(tz => tz === zoneId || tz === zoneName)) {
                        return sum + (app.weight || 0);
                      }
                      return sum;
                    }, 0);
                    
                    // Convert to t/ha
                    const appliedRate = zoneSurfaceArea > 0 ? parseFloat((appliedBiocharKg / 1000 / zoneSurfaceArea).toFixed(2)) : 0;
                    
                    return(
                      <Fragment key={zoneRefId(zone)}>
                        {index !== 0 && <div className={styles.separatorLine}/>}
                        <div className={styles.gaugeItem}>
                          <Gauge progress={appliedRate} progressMax={zone.targeted_rate} unit={"t/ha"}/>
                          <div className={styles.gaugeLabel}>
                            {zone.field_type ? zone.field_type.charAt(0).toUpperCase() + zone.field_type.slice(1) : ""}
                            <div className={styles.fieldType}><img src={zone.field_type === "grassland" ? iconGrassland : iconArable} className={styles.fieldTypeIcon}/>{zoneRefName(zone)}</div>

                            <div className={styles.targetRate}>{zone.name}Target Rate: <span className={styles.targetValue}>{zone.targeted_rate} t/ha</span></div>
                            <div className={styles.targetRate}>Total Applied: <span className={styles.targetValue}>{(appliedBiocharKg / 1000).toFixed(2)} t</span></div>
                          </div>
                          <div className={styles.gaugeCheckboxContainer}>
                            <Checkbox 
                              checked={visibleZones[zoneId] !== false}
                              text="Show Applications"
                              onPress={() => toggleZoneVisibility(zoneId)}
                            />
                          </div>
                        </div>
                      </Fragment>
                    );
                  }
                })}
                </div>
                
              </div>


              
            </div>
            <div className={styles.detailsRow}>
              


              {/* Applications List */}
              <div className={styles.applicationsContainer}>
               
                {(() => {
                  // Get all applications from zones
                  const allApplications = getAllApplicationsFromZones();
                  
                  // Filter applications based on visible zones
                  const filteredApplications = allApplications.length > 0 
                    ? allApplications.filter((app) => {
                        // Check if any of the app's targeted zones are visible
                        if (!app.targetedZones || app.targetedZones.length === 0) return true; // Show apps with no zone targeting
                        return app.targetedZones.some(zoneRef => {
                          // zoneRef could be a zone ID string or zone name
                          // Check against visible zone IDs
                          return Object.entries(visibleZones).some(([zoneId, isVisible]) => {
                            if (!isVisible) return false;
                            // Match by ID or by zone name
                            const zone = targetedZonesOf(props.selectedPlan).find(z => 
                              zoneRefId(z) === zoneId
                            );
                            if (!zone) return false;
                            return zoneRef === zoneId || zoneRef === zoneRefName(zone) || zoneRef === zoneRefId(zone);
                          });
                        });
                      })
                    : [];

                  // Calculate total applied biochar from filtered applications
                  const totalAppliedKg = filteredApplications.reduce((sum, app) => sum + (app.weight || 0), 0);
                  const totalAppliedT = (totalAppliedKg / 1000).toFixed(2);

                  return (
                    <>
                      <div className={styles.headerLineContainer}>
                        <div className={styles.headerContainer}>
                          Biochar Applications
                        </div>
                        <div className={styles.totalAppliedContainer}>
                          <span className={styles.totalAppliedLabel}>Total Applied: </span>
                          <span className={styles.totalAppliedValue}>{totalAppliedT} t</span>
                        </div>
                      </div>
                      <div className={styles.applicationRows}>
                        {filteredApplications.length > 0 ? 
                          filteredApplications.map((app, index) => (
                            <ApplicationCard key={index} app={app} isPlaceholder={false}/>
                          )) : 
                          <ApplicationCard 
                          isPlaceholder={true}/>}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </Module>
      </div>
    </div>
  );
};

export default PlanDetails;